<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { Material } from "@/types/Views/MaterialDatabase/Material";
import useLibraryEnums, { KeyedIdentityEnum, KeyedStringEnum } from "@/providers/Materials/LibraryEnumsProvider";
import { useLocalization } from "@/providers/Localization";
import { useCategoryTranslations } from "@/dataProviders/CategoryTranslations";

import { getStageTooltip } from "@/services/Views/MaterialDatabase/StageTooltip";
import { getIndicatorTooltip } from "@/services/Views/MaterialDatabase/IndicatorTooltip";
import { resolveIndicatorType } from "@/utils/constructionMaterial";

import SwitchTabs from "@/components/Reusables/Switches/SwitchTabs.vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";
import Table from "@/components/Reusables/Table/Table.vue";
import CompareGraph from "../Modal/CompareGraph";
import { ChevronDown, Download, X } from "@lucide/vue";

import format from "@/services/Utils/format";
import i18n from "@/services/i18n";

const libraryEnums = useLibraryEnums();
const localization = useLocalization();
const categoryTranslations = useCategoryTranslations();

const props = defineProps<{
  materials: Material[];
  // Clear the whole comparison. The host decides what that means (close the modal, or leave the page).
  onClear: () => void;
  // Called when a material is removed from the comparison, so the host can keep its selection / URL in
  // sync. Optional — omit to make the comparison read-only.
  onRemove?: (id: string) => void;
}>();

// Local, mutable copy of the materials so removing a column updates the tables, charts and PDF
// immediately. Seeded from props and re-seeded if the parent ever swaps the array.
const compareMaterials = ref<Material[]>([...props.materials]);
watch(
  () => props.materials,
  (materials) => {
    compareMaterials.value = [...materials];
  }
);

const removeMaterial = (id?: string) => {
  if (!id) return;
  compareMaterials.value = compareMaterials.value.filter((material) => material.id !== id);
  props.onRemove?.(id);
};

// ── Tabs ─────────────────────────────────────────────────────────────────────

type TabKey = "overview" | "stages";

const activeTab = ref<TabKey>("overview");

// Template refs for chart containers — used by downloadComparison to embed charts in the PDF
const overviewChartsEl = ref<HTMLElement | null>(null);
const stagesChartsEl = ref<HTMLElement | null>(null);

// SwitchTabs is a boolean segmented control; map it onto the two-way activeTab.
const stagesModel = computed<boolean>({
  get: () => activeTab.value === "stages",
  set: (isStages) => {
    activeTab.value = isStages ? "stages" : "overview";
  },
});

// ── Enums ─────────────────────────────────────────────────────────────────────

const isDoneLoading = ref(false);
const declaredUnitEnums = ref<KeyedIdentityEnum | undefined>();
const massUnitEnums = ref<KeyedIdentityEnum | undefined>();
const specFormEnums = ref<KeyedIdentityEnum | undefined>();
const indicatorEnums = ref<KeyedStringEnum | undefined>();
const indicatorTypeEnums = ref<KeyedStringEnum | undefined>();

onMounted(async () => {
  [declaredUnitEnums.value, massUnitEnums.value, specFormEnums.value, indicatorEnums.value, indicatorTypeEnums.value] =
    await Promise.all([
      libraryEnums.getDeclaredUnitEnums(),
      libraryEnums.getMassUnitEnums(),
      libraryEnums.getSpecificationFormEnums(),
      libraryEnums.getIndicatorEnums(),
      libraryEnums.getIndicatorTypeEnums(),
    ]);
  isDoneLoading.value = true;
});

// ── Material grouping ─────────────────────────────────────────────────────────

const containsA2 = ref(false);
const materialsByUnit = ref<Record<string, Material[]>>({});

const rebuild = () => {
  const byUnit: Record<string, Material[]> = {};
  let hasA2 = false;

  (compareMaterials.value ?? []).forEach((m, idx) => {
    const material = { ...m, compareIndex: idx + 1 };
    if (material.epdInfo?.epdSpecificationForm === 1) hasA2 = true;
    const unit = String(material.declaredUnit?.declaredUnit ?? "UNKNOWN");
    (byUnit[unit] ??= []).push(material);
  });

  materialsByUnit.value = byUnit;
  containsA2.value = hasA2;
};

watch(
  () => compareMaterials.value,
  () => rebuild(),
  { immediate: true }
);

const allSameDeclaredUnit = computed(() => {
  if (compareMaterials.value.length <= 1) return true;
  const firstUnit = compareMaterials.value[0]?.declaredUnit?.declaredUnit;
  return compareMaterials.value.every((m) => m.declaredUnit?.declaredUnit === firstUnit);
});

// ── Indicator selection ───────────────────────────────────────────────────────

const indicatorOptions = computed(() => {
  if (!indicatorTypeEnums.value || !indicatorEnums.value) return [];

  const indicators = new Set<number>();
  for (const m of compareMaterials.value) {
    for (const r of m.lciaResults) {
      for (const ind of r.indicators) {
        indicators.add(ind.type);
      }
    }
  }

  return Array.from(indicators)
    .sort((a, b) => a - b)
    .map((type) => ({
      value: type,
      label: `${indicatorTypeEnums.value![String(type)] ?? String(type)} - ${getIndicatorTooltip(type)} (${getUnitForIndicator(
        type
      )})`,
    }));
});

const selectedIndicator = ref(12); // GWP Total default

watch(
  indicatorOptions,
  (opts) => {
    if (opts.length === 0) return;
    const preferred = opts.find((o) => o.value === 12) ?? opts.find((o) => o.value === 0) ?? opts[0];
    if (preferred) selectedIndicator.value = preferred.value;
  },
  { immediate: true }
);

function getUnitForIndicator(type: number): string {
  const code = indicatorTypeEnums.value?.[String(type)];
  if (!code) return "—";
  return indicatorEnums.value?.[code] ?? "—";
}

const currentUnit = computed(() => getUnitForIndicator(selectedIndicator.value));

function getChartTitle(unitKey: string): string {
  const unitName = declaredUnitEnums.value?.[unitKey]?.DisplayName ?? unitKey;
  return `Comparing materials — ${unitName} as declared unit`;
}

// ── Metadata helpers ──────────────────────────────────────────────────────────

function getDeclaredUnitName(idx: number): string {
  return declaredUnitEnums.value?.[String(idx)]?.Enum ?? "—";
}

function getMassUnitName(idx: number): string {
  return massUnitEnums.value?.[String(idx)]?.DisplayName ?? "—";
}

function getSpecFormName(idx: number): string {
  return specFormEnums.value?.[String(idx)]?.DisplayName ?? "—";
}

function formatDate(date?: string | null): string {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString();
  } catch {
    return date;
  }
}

const categoryLabel = (category?: string | null) =>
  category ? categoryTranslations.translate(category, [i18n.global.locale.value]).value : "";

// Single source of truth for the material-attribute rows — rendered both on-screen (the Table
// below) and in the PDF export (downloadComparison). Values use the "—" empty fallback; the PDF
// runs each through safe(), which maps "—" → "-" for jsPDF's Latin-1 font.
const metadataRows: { label: string; value: (m: Material) => string }[] = [
  { label: "Declared unit", value: (m) => getDeclaredUnitName(m.declaredUnit.declaredUnit) },
  { label: "Mass unit", value: (m) => getMassUnitName(m.declaredUnit.massUnit) },
  {
    label: "Suggested lifetime",
    value: (m) =>
      m.expectedLifespan === null || m.expectedLifespan === 0 ? "No lifetime suggested" : `${m.expectedLifespan} years`,
  },
  { label: "Datasource", value: (m) => m.datasourceNames?.join(", ") || "—" },
  { label: "Owner", value: (m) => m.declarationOwnerName || "—" },
  { label: "Valid from", value: (m) => formatDate(m.epdInfo?.publicationDate) },
  { label: "Valid to", value: (m) => formatDate(m.epdInfo?.validTo) },
  { label: "Material category", value: (m) => categoryLabel(m.category) || "—" },
  { label: "EPD standard", value: (m) => (m.epdInfo ? getSpecFormName(m.epdInfo.epdSpecificationForm) : "—") },
];

function getMaterialName(m: Material): string {
  return localization.getEpdTranslation(m.names, m.name);
}

// ── Per-kg comparison ─────────────────────────────────────────────────────────

const materialsWithKgMass = computed(() =>
  compareMaterials.value.filter((m) => m.declaredUnit.mass !== null && m.declaredUnit.mass !== 0)
);

function getEmissionPerKg(m: Material, indicatorType: number): number {
  const mass = m.declaredUnit.mass;
  if (!mass) return 0;
  const type = resolveIndicatorType(m, indicatorType);
  const total = m.lciaResults
    .filter((r) => ![1, 2, 3].includes(r.moduleTypeEnum))
    .reduce((sum, r) => {
      const ind = r.indicators.find((i) => i.type === type);
      return sum + (ind?.value ?? 0);
    }, 0);
  return total / mass;
}

// ── Static stage list (all 15 standard lifecycle stages) ─────────────────────

const ALL_STAGES = [0, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
const NON_B_STAGES = [0, 4, 5, 13, 14, 15, 16, 17];

// ── Stage selection (Tab 2) ───────────────────────────────────────────────────

const STAGE_PRESETS: { key: string; label: string; stages: number[] | null }[] = [
  { key: "full", label: "Full LCA (A–D)", stages: null },
  {
    key: "cradle_to_grave",
    label: "Cradle to grave (A1–C4, excl. D)",
    stages: [0, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
  },
  { key: "a1a5", label: "Cradle to gate + Installation (A1–A5)", stages: [0, 4, 5] },
  { key: "a1a4", label: "Cradle to gate + Transport (A1–A4)", stages: [0, 4] },
  { key: "a1a3", label: "Cradle to gate (A1–A3)", stages: [0] },
  { key: "custom", label: "Custom", stages: null },
];

const selectedPreset = ref("full");
const selectedStages = ref<number[]>([...ALL_STAGES]);

function applyPreset(presetKey: string) {
  selectedPreset.value = presetKey;
  const preset = STAGE_PRESETS.find((p) => p.key === presetKey);
  if (!preset || preset.key === "custom") return;
  if (preset.stages === null) {
    selectedStages.value = [...ALL_STAGES];
  } else {
    selectedStages.value = ALL_STAGES.filter((s) => preset.stages!.includes(s));
  }
}

function toggleStage(stage: number) {
  const idx = selectedStages.value.indexOf(stage);
  if (idx === -1) {
    selectedStages.value.push(stage);
  } else {
    selectedStages.value.splice(idx, 1);
  }
  selectedPreset.value = "custom";
}

async function downloadComparison() {
  const indicatorLabel =
    indicatorOptions.value.find((o) => o.value === selectedIndicator.value)?.label ?? String(selectedIndicator.value);
  const unit = currentUnit.value;
  const date = new Date().toLocaleDateString();

  const fmt = (n: number) => {
    if (n === 0) return "0";
    const abs = Math.abs(n);
    const decimals = abs < 0.001 ? 6 : abs < 1 ? 4 : 2;
    return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // jsPDF's built-in Helvetica only covers Latin-1. Characters outside that range
  // (subscripts, superscripts, special typographic chars) get rendered letter-by-letter
  // with broken spacing. Replace the common offenders with safe ASCII equivalents.
  const safe = (s: string) =>
    s
      .replace(/[\u2080-\u2089]/g, (c) => String(c.charCodeAt(0) - 0x2080)) // subscript digits ₀–₉ → 0–9
      .replace(/[\u2070\u00B9\u00B2\u00B3\u2074-\u2079]/g, (c) => {
        const map: Record<string, string> = {
          "\u2070": "0",
          "\u00B9": "1",
          "\u00B2": "2",
          "\u00B3": "3",
          "\u2074": "4",
          "\u2075": "5",
          "\u2076": "6",
          "\u2077": "7",
          "\u2078": "8",
          "\u2079": "9",
        };
        return map[c] ?? c;
      }) // superscript digits
      .replace(/\u2013/g, "-") // en dash
      .replace(/\u2014/g, "-") // em dash
      .replace(/[\u2018\u2019]/g, "'") // curly single quotes
      .replace(/[\u201C\u201D]/g, '"') // curly double quotes
      .replace(/\u2026/g, "...") // ellipsis
      .replace(/[^\x00-\xFF]/g, "?"); // anything else outside Latin-1 → ?

  // ── Palette ──────────────────────────────────────────────────────────────────
  const BLUE: [number, number, number] = [32, 54, 255]; // app-blue-600
  const BLUE_LIGHT: [number, number, number] = [235, 244, 255]; // app-blue-50
  const NAVY: [number, number, number] = [30, 41, 59]; // slate-800
  const HEAD_BG: [number, number, number] = [51, 65, 85]; // slate-700 — softer than navy
  const MUTED: [number, number, number] = [100, 116, 139]; // slate-500
  const ROW_ALT: [number, number, number] = [248, 250, 252]; // slate-50
  const TOTAL_BG: [number, number, number] = [241, 245, 249]; // slate-100 — neutral, not blue
  const WHITE: [number, number, number] = [255, 255, 255];
  const BORDER: [number, number, number] = [226, 232, 240]; // slate-200

  const PAGE_W = 297;
  const MARGIN = 14;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const materialNames = compareMaterials.value.map((m) => safe(getMaterialName(m)));

  const headStyle = {
    fillColor: HEAD_BG,
    textColor: WHITE,
    fontStyle: "bold" as const,
    fontSize: 8,
    cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
  };
  const bodyStyle = {
    fontSize: 8,
    textColor: NAVY,
    cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    lineColor: BORDER,
    lineWidth: 0.1,
  };
  const altStyle = { fillColor: ROW_ALT };

  // Helper: draw a section heading with a left blue accent bar
  const sectionHeading = (text: string, y: number) => {
    doc.setFillColor(...BLUE);
    doc.rect(MARGIN, y - 4, 2, 6, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.text(text, MARGIN + 5, y);
    doc.setTextColor(...NAVY);
  };

  // Helper: add footer on every page
  const addFooter = () => {
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const y = 205;
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.3);
      doc.line(MARGIN, y - 2, PAGE_W - MARGIN, y - 2);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...MUTED);
      doc.text(`Indicator: ${safe(indicatorLabel)}`, MARGIN, y + 2);
      doc.text(`Page ${i} of ${pageCount}`, PAGE_W - MARGIN, y + 2, { align: "right" });
    }
  };

  // ── Header ───────────────────────────────────────────────────────────────────
  // Thin blue rule at the very top
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, PAGE_W, 3, "F");

  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Material Comparison Report", MARGIN, 13);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...MUTED);
  doc.text(`Generated ${date}`, PAGE_W - MARGIN, 9, { align: "right" });
  doc.text(safe(indicatorLabel), PAGE_W - MARGIN, 15, { align: "right" });

  // Thin divider below the title
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 19, PAGE_W - MARGIN, 19);

  doc.setTextColor(...NAVY);

  // ── Metadata table ──────────────────────────────────────────────────────────
  sectionHeading("Material attributes", 27);

  const metaRows = metadataRows.map((row) => [row.label, ...compareMaterials.value.map((m) => safe(row.value(m)))]);

  autoTable(doc, {
    startY: 30,
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: CONTENT_W,
    head: [["Attribute", ...materialNames]],
    body: metaRows,
    headStyles: headStyle,
    bodyStyles: bodyStyle,
    alternateRowStyles: altStyle,
    columnStyles: { 0: { fontStyle: "bold", fillColor: BLUE_LIGHT, textColor: NAVY } },
    styles: { overflow: "linebreak" },
  });

  // ── Emissions by lifecycle stage ─────────────────────────────────────────────
  const afterMeta = (doc as any).lastAutoTable.finalY + 10;
  sectionHeading(`Emissions by lifecycle stage (${safe(unit)})`, afterMeta);

  const stageRows = selectedStages.value.map((stage) => [
    safe(getStageTooltip(stage)),
    ...compareMaterials.value.map((m) => {
      const lciaResult = m.lciaResults.find((r) => r.moduleTypeEnum === stage);
      const type = resolveIndicatorType(m, selectedIndicator.value);
      const ind = lciaResult?.indicators.find((i) => i.type === type);
      return fmt(ind?.value ?? 0);
    }),
  ]);

  const totalRow = ["Total", ...materialTotals.value.map((t) => fmt(t.total))];

  const valueColStyle = { halign: "right" as const };
  const stageColStyles: Record<number, object> = {
    0: { fontStyle: "bold", fillColor: BLUE_LIGHT, textColor: NAVY, cellWidth: 32 },
  };
  compareMaterials.value.forEach((_, i) => {
    stageColStyles[i + 1] = valueColStyle;
  });

  autoTable(doc, {
    startY: afterMeta + 3,
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: CONTENT_W,
    head: [["Stage", ...materialNames]],
    body: [...stageRows, totalRow],
    headStyles: { ...headStyle, overflow: "linebreak" },
    bodyStyles: bodyStyle,
    alternateRowStyles: altStyle,
    columnStyles: stageColStyles,
    styles: { overflow: "linebreak" },
    didParseCell: (data) => {
      if (data.row.index === stageRows.length) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = TOTAL_BG;
        data.cell.styles.textColor = NAVY;
      }
    },
  });

  // ── Per-kg table (only when units differ) ──────────────────────────────────
  if (!allSameDeclaredUnit.value && materialsWithKgMass.value.length >= 2) {
    const afterStages = (doc as any).lastAutoTable.finalY + 10;
    sectionHeading("Per kg comparison", afterStages);

    autoTable(doc, {
      startY: afterStages + 3,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: CONTENT_W,
      head: [["Material", `Total per kg (${safe(unit)}/kg)`]],
      body: materialsWithKgMass.value.map((m) => [safe(getMaterialName(m)), fmt(getEmissionPerKg(m, selectedIndicator.value))]),
      headStyles: headStyle,
      bodyStyles: bodyStyle,
      alternateRowStyles: altStyle,
      columnStyles: { 1: { halign: "right" } },
      styles: { overflow: "linebreak" },
    });
  }

  // ── Charts ────────────────────────────────────────────────────────────────────
  // ECharts uses SVGRenderer so we serialize the SVG elements directly to canvas.
  const chartsEl = activeTab.value === "overview" ? overviewChartsEl.value : stagesChartsEl.value;
  if (chartsEl) {
    const svgEls = Array.from(chartsEl.querySelectorAll<SVGSVGElement>("svg"));
    for (const svgEl of svgEls) {
      const rect = svgEl.getBoundingClientRect();
      const W = Math.round(rect.width) || 800;
      const H = Math.round(rect.height) || 400;

      try {
        // Parse → set explicit px dimensions → re-serialize so the img knows its size
        const serializer = new XMLSerializer();
        const parser = new DOMParser();
        const parsed = parser.parseFromString(serializer.serializeToString(svgEl), "image/svg+xml");
        const root = parsed.documentElement;
        root.setAttribute("width", String(W));
        root.setAttribute("height", String(H));
        const svgStr = serializer.serializeToString(root);

        const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const imgData = await new Promise<string>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const SCALE = 2;
            const c = document.createElement("canvas");
            c.width = W * SCALE;
            c.height = H * SCALE;
            const ctx = c.getContext("2d")!;
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, c.width, c.height);
            ctx.drawImage(img, 0, 0, W * SCALE, H * SCALE);
            URL.revokeObjectURL(url);
            resolve(c.toDataURL("image/png"));
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("SVG render failed"));
          };
          img.src = url;
        });

        const pdfW = CONTENT_W;
        const pdfH = pdfW * (H / W);

        doc.addPage();
        const headingY = MARGIN + 5;
        sectionHeading(activeTab.value === "overview" ? "Emissions overview" : "Emissions by lifecycle stage", headingY);

        const chartStartY = headingY + 8;
        const maxH = 210 - chartStartY - MARGIN;
        const finalH = Math.min(pdfH, maxH);
        const finalW = pdfH > maxH ? (maxH / pdfH) * pdfW : pdfW;

        doc.addImage(imgData, "PNG", MARGIN, chartStartY, finalW, finalH);
      } catch {
        // Skip chart on capture error — tables are still complete
      }
    }
  }

  addFooter();
  doc.save(`material-comparison-${date.replace(/\//g, "-")}.pdf`);
}

// Per-material totals for selected stages + indicator
const materialTotals = computed(() =>
  compareMaterials.value.map((m) => {
    const type = resolveIndicatorType(m, selectedIndicator.value);
    const total = selectedStages.value.reduce((sum, stage) => {
      const lciaResult = m.lciaResults.find((r) => r.moduleTypeEnum === stage);
      const ind = lciaResult?.indicators.find((i) => i.type === type);
      return sum + (ind?.value ?? 0);
    }, 0);
    return { name: getMaterialName(m), total };
  })
);
</script>

<template>
  <!-- No internal scroll: the modal content flows at natural height and ModalRoot's own
       overflow-auto wrapper is the single scroller. Prevents nested/double scrollbars. -->
  <div class="gap-px-16 relative flex w-full flex-col">
    <!-- Heading -->
    <div class="gap-px-16 flex shrink-0 items-start justify-between">
      <div class="gap-px-4 flex flex-col">
        <h2 class="t-sh3 text-type-default">Compare</h2>
        <span
          v-if="compareMaterials.length"
          class="t-caption text-type-muted">
          {{ compareMaterials.length }} {{ compareMaterials.length === 1 ? "material" : "materials" }} selected
        </span>
      </div>
      <!-- Clear compare lives in the header (not a footer) so it's reachable without scrolling past the
           full comparison tables/charts. -->
      <button
        type="button"
        class="rounded-8 bg-slate-0 text-type-default focus-ring interactive-active duration-fast ease-standard gap-px-8 p-px-8 pr-px-12 ring-slate-250 inline-flex h-fit shrink-0 items-center justify-center ring-1 transition-all hover:bg-slate-100 hover:ring-slate-300"
        @click="props.onClear()">
        <X
          class="icon-sm shrink-0"
          :stroke-width="1.5" />
        <span class="t-small-bold leading-none">Clear compare</span>
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="!isDoneLoading"
      class="flex min-h-[320px] items-center justify-center">
      <LoadingLogoSpinner />
    </div>

    <!-- Below 2 materials there's nothing to compare — show a hint instead of a broken single-column table. -->
    <div
      v-else-if="compareMaterials.length < 2"
      class="flex min-h-[320px] items-center justify-center">
      <p class="t-body text-type-muted">Select at least 2 materials to compare.</p>
    </div>

    <template v-else>
      <!-- Controls: view switcher · indicator select · download -->
      <div class="gap-px-12 flex shrink-0 flex-wrap items-center">
        <SwitchTabs
          v-model:value="stagesModel"
          size="fit"
          class="shrink-0">
          <template #left>Overview</template>
          <template #right>Lifecycle Stages</template>
        </SwitchTabs>

        <div class="gap-px-8 ml-auto flex items-center">
          <label class="t-small-bold text-type-default shrink-0">Indicator</label>
          <div class="relative">
            <select
              v-model="selectedIndicator"
              class="focus-input t-small-bold text-type-default bg-slate-0 pl-px-12 pr-px-32 py-px-6 duration-fast ease-standard rounded-8 max-w-xs cursor-pointer appearance-none border border-slate-200 transition-colors hover:border-slate-300">
              <option
                v-for="opt in indicatorOptions"
                :key="opt.value"
                :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
            <ChevronDown
              class="icon-sm text-type-muted right-px-12 pointer-events-none absolute top-1/2 -translate-y-1/2"
              :stroke-width="1.5" />
          </div>
          <button
            type="button"
            class="focus-ring interactive-active rounded-8 t-small-bold duration-fast ease-standard gap-px-8 bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover px-px-16 py-px-8 flex shrink-0 items-center text-white transition-colors"
            @click="downloadComparison">
            <Download
              class="icon-sm shrink-0"
              :stroke-width="1.5" />
            Download
          </button>
        </div>
      </div>

      <!-- Panels (natural height — the modal scrolls as one via ModalRoot's wrapper) -->
      <div class="min-w-0">
        <!-- ── Overview ──────────────────────────────────────────────────── -->
        <div
          v-if="activeTab === 'overview'"
          class="gap-y-px-32 flex flex-col">
          <!-- Metadata comparison table. overflow-y-hidden stops overflow-x-auto from computing
               overflow-y to `auto` and trapping the vertical wheel — it chains to the panel instead. -->
          <div class="rounded-8 overflow-x-auto overflow-y-hidden border border-slate-200">
            <Table
              caption="Material attribute comparison"
              table-class="w-full min-w-[480px] text-sm">
              <template #thead>
                <thead>
                  <tr class="border-b border-slate-200 bg-slate-100">
                    <th
                      scope="col"
                      class="text-type-muted t-caption-bold py-3 pl-4 text-left tracking-wide uppercase">
                      Attribute
                    </th>
                    <th
                      v-for="m in compareMaterials"
                      :key="m.id"
                      scope="col"
                      class="text-type-default t-small-bold px-4 py-3 text-left">
                      <div class="flex items-start justify-between gap-2">
                        <span class="line-clamp-2">{{ getMaterialName(m) }}</span>
                        <button
                          type="button"
                          class="focus-ring rounded-4 text-type-muted hover:text-app-red-600 duration-fast ease-standard shrink-0 p-0.5 transition-colors"
                          :aria-label="`Remove ${getMaterialName(m)} from comparison`"
                          @click="removeMaterial(m.id)">
                          <X
                            class="icon-xs"
                            :stroke-width="1.5" />
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
              </template>
              <template #tbody>
                <tbody>
                  <tr
                    v-for="(row, index) in metadataRows"
                    :key="row.label"
                    :class="['hover:bg-slate-100', index < metadataRows.length - 1 ? 'border-b border-slate-200' : '']">
                    <td class="text-type-muted t-small py-3 pl-4">{{ row.label }}</td>
                    <td
                      v-for="m in compareMaterials"
                      :key="m.id"
                      class="text-type-default t-small px-4 py-3">
                      {{ row.value(m) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </Table>
          </div>

          <!-- Emission charts per declared unit -->
          <div
            ref="overviewChartsEl"
            class="gap-y-px-48 flex flex-col">
            <CompareGraph
              v-for="(materialGroup, unitKey) in materialsByUnit"
              :key="`${unitKey}-${materialGroup.length}`"
              :materials="materialGroup"
              :chart-title="getChartTitle(String(unitKey))"
              :indicator-unit="currentUnit"
              :selected-indicator="selectedIndicator"
              :selected-stages="NON_B_STAGES" />
          </div>

          <!-- Per-kg comparison when units differ -->
          <div
            v-if="!allSameDeclaredUnit && materialsWithKgMass.length >= 2"
            class="rounded-8 border border-slate-200 bg-slate-100 p-4">
            <h5 class="t-sh5 mb-px-12 text-type-default">Per kg comparison</h5>
            <p class="t-caption mb-px-16 text-type-muted">
              Materials have different declared units. Total emissions per kg for cross-unit comparison.
            </p>
            <Table
              caption="Per-kg emission comparison"
              table-class="w-full text-sm">
              <template #thead>
                <thead>
                  <tr class="border-b border-slate-200">
                    <th
                      scope="col"
                      class="text-type-muted t-small-bold py-2 text-left">
                      Material
                    </th>
                    <th
                      scope="col"
                      class="text-type-muted t-small-bold px-4 py-2 text-right">
                      Total per kg ({{ currentUnit }}/kg)
                    </th>
                  </tr>
                </thead>
              </template>
              <template #tbody>
                <tbody>
                  <tr
                    v-for="m in materialsWithKgMass"
                    :key="m.id"
                    class="border-b border-slate-200 hover:bg-slate-100">
                    <td class="text-type-default t-small py-2">{{ getMaterialName(m) }}</td>
                    <td class="text-type-default t-small-bold px-4 py-2 text-right">
                      {{ format.decimalNumber(getEmissionPerKg(m, selectedIndicator), 4) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </Table>
          </div>

          <span
            v-if="containsA2"
            class="t-caption text-type-muted italic">
            <span class="t-caption-bold text-type-default">Disclaimer: </span>
            According to the EN 15804+A2 standard, a comparison of EPD data is only possible if the building context and the
            product-specific characteristics of performance are taken into account. EPD that are not in a building context are not
            tools to compare construction products.
          </span>
        </div>

        <!-- ── Lifecycle Stages ──────────────────────────────────────────── -->
        <div
          v-else
          class="gap-y-px-24 flex flex-col">
          <!-- Stage filter controls -->
          <div class="rounded-8 border border-slate-200 bg-slate-100 p-4">
            <div class="gap-px-16 flex flex-wrap items-center">
              <label class="t-small-bold text-type-default shrink-0">Calculation type:</label>
              <div class="relative">
                <select
                  :value="selectedPreset"
                  class="focus-input t-small-bold text-type-default bg-slate-0 pl-px-12 pr-px-32 py-px-6 duration-fast ease-standard rounded-8 cursor-pointer appearance-none border border-slate-200 transition-colors hover:border-slate-300"
                  @change="applyPreset(($event.target as HTMLSelectElement).value)">
                  <option
                    v-for="preset in STAGE_PRESETS"
                    :key="preset.key"
                    :value="preset.key">
                    {{ preset.label }}
                  </option>
                </select>
                <ChevronDown
                  class="icon-sm text-type-muted right-px-12 pointer-events-none absolute top-1/2 -translate-y-1/2"
                  :stroke-width="1.5" />
              </div>
            </div>

            <!-- Individual stage toggles -->
            <div class="mt-px-12 gap-px-8 flex flex-wrap">
              <button
                v-for="stage in ALL_STAGES"
                :key="stage"
                type="button"
                :aria-pressed="selectedStages.includes(stage)"
                class="focus-ring interactive-active gap-px-6 px-px-8 py-px-4 rounded-4 t-caption duration-fast ease-standard flex items-center border transition-colors"
                :class="
                  selectedStages.includes(stage)
                    ? 'bg-slate-0 text-type-default border-slate-300'
                    : 'text-type-muted border-slate-200 bg-slate-100 opacity-60'
                "
                @click="toggleStage(stage)">
                {{ getStageTooltip(stage) }}
              </button>
            </div>
          </div>

          <!-- Total emission per material -->
          <div class="rounded-8 border border-slate-200 bg-slate-100 p-4">
            <h5 class="t-sh5 mb-px-12 text-type-default">Total emissions for selected stages</h5>
            <Table
              caption="Total emissions for selected lifecycle stages"
              table-class="w-full text-sm">
              <template #thead>
                <thead>
                  <tr class="border-b border-slate-200">
                    <th
                      scope="col"
                      class="text-type-muted t-small-bold py-2 text-left">
                      Material
                    </th>
                    <th
                      scope="col"
                      class="text-type-muted t-small-bold px-4 py-2 text-right">
                      Total ({{ currentUnit }})
                    </th>
                  </tr>
                </thead>
              </template>
              <template #tbody>
                <tbody>
                  <tr
                    v-for="item in materialTotals"
                    :key="item.name"
                    class="border-b border-slate-200 hover:bg-slate-100">
                    <td class="text-type-default t-small py-2">{{ item.name }}</td>
                    <td class="text-type-default t-small-bold px-4 py-2 text-right">
                      {{ format.decimalNumber(item.total, 4) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </Table>
          </div>

          <!-- Charts per unit group with selected stages -->
          <div
            ref="stagesChartsEl"
            class="gap-y-px-48 flex flex-col">
            <CompareGraph
              v-for="(materialGroup, unitKey) in materialsByUnit"
              :key="`stages-${unitKey}-${materialGroup.length}`"
              :materials="materialGroup"
              :chart-title="getChartTitle(String(unitKey))"
              :indicator-unit="currentUnit"
              :selected-indicator="selectedIndicator"
              :selected-stages="selectedStages" />
          </div>

          <span
            v-if="containsA2"
            class="t-caption text-type-muted italic">
            <span class="t-caption-bold text-type-default">Disclaimer: </span>
            According to the EN 15804+A2 standard, a comparison of EPD data is only possible if the building context and the
            product-specific characteristics of performance are taken into account. EPD that are not in a building context are not
            tools to compare construction products.
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.scrollbar::-webkit-scrollbar {
  width: 8px;
}
.scrollbar::-webkit-scrollbar-track {
  border-radius: 100vh;
  background: var(--color-slate-100); /* migrated to token – was #f1f5f9 */
}
.scrollbar::-webkit-scrollbar-thumb {
  border-radius: 100vh;
  background: var(--color-type-muted); /* migrated to token – was #94a3b8 */
}
</style>
